import { useEffect, useState, useRef } from "react";
import * as React from "react";
import StorageService from "../../../App/StorageService"
import { StorageKey } from "../../../App/enums"
import "../../../Styles/main.scss"

function Image(props: React.ImgHTMLAttributes<HTMLImageElement>) {
    const [isVisible, setIsVisible] = useState(false)
    const [imageSrc, setImageSrc] = useState('')
    const [hasError, setHasError] = useState(false)
    const abortControllerRef = useRef<AbortController | null>(null)

    const accessToken = StorageService.getItem(StorageKey.accessToken) as string

    const addCacheItem = (key: string, value: string, ttl: number) => {
        const expiry = Date.now() + ttl;
        const item = { value, expiry };
        try {
            sessionStorage.setItem(key, JSON.stringify(item));
        } catch (e) {
            // Item too large or storage quota exceeded
        }
    };

    const getCacheItem = (key: string) => {
        const itemStr = sessionStorage.getItem(key)
        if (!itemStr) {
            return null
        }
        const item = JSON.parse(itemStr)
        const now = new Date()
        if (now.getTime() > item.expiry) {
            sessionStorage.removeItem(key)
            return null
        }
        return item.value
    }
    

    const getBase64Image = async (res: Response): Promise<string | ArrayBuffer | null> => {
        const blob = await res.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    useEffect(() => {
        let src = props.src as string ?? "";
        
        // Reset error state when src changes
        setHasError(false);
        setIsVisible(false);
        setImageSrc("");
        
        if (!src) {
            setIsVisible(true);
            return;
        }

        // Normalize dev URLs
        if (src.includes("3000")) {
            src = src.replace("3000", "8000").replace("https://", "http://");
        }

        // Check if image needs to be fetched with auth
        const isMedia =
            src.startsWith("/media") ||
            src.includes(process.env.REACT_APP_BASE_API_URL!.replace("api", ""));

        if (isMedia) {
            // Use full URL as cache key to avoid collisions
            const cacheKey = src;

            const cachedItem = getCacheItem(cacheKey);
            if (cachedItem) {
                setImageSrc(cachedItem);
                setIsVisible(true);
                return;
            }

            // Ensure absolute URL for localhost
            if (
                window.location.hostname === "localhost" &&
                src.startsWith("/media")
            ) {
                src = process.env.REACT_APP_BASE_API_URL!.replace("api", "") + src;
            }

            src = src.replace("//media", "/media");

            // Cancel previous request if one exists
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            
            // Create new AbortController for this fetch
            const controller = new AbortController();
            abortControllerRef.current = controller;
            
            fetch(src, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                signal: controller.signal,
            })
                .then((res) => {
                    if (!res.ok) {
                        throw new Error(`Image failed to load: ${res.status}`);
                    }
                    return getBase64Image(res);
                })
                .then((imgString) => {
                    if (imgString) {
                        setImageSrc(imgString as string);
                        setIsVisible(true);
                        addCacheItem(cacheKey, imgString as string, 3600000);
                    }
                })
                .catch((error) => {
                    if (error.name !== 'AbortError') {
                        console.warn("Image load failed:", src, error);
                        setHasError(true);
                        setIsVisible(true);
                    }
                });

            // Cleanup: abort the fetch request if component unmounts or src changes
            return () => {
                if (controller.signal.aborted === false) {
                    controller.abort();
                }
            };
        } else {
            setIsVisible(true);
            setImageSrc(src);
        }
    }, [props.src, accessToken]);

    if(!imageSrc){
        if (hasError) {
            return <div {...props} style={{...props.style, display: isVisible ? 'block' : 'none', backgroundColor: '#f0f0f0', color: '#999'}} title="Image failed to load">📷</div>
        }
        // Add loading class for profile and cover images
        const isProfileImage = props.className?.includes('image__profile')
        const isCoverImage = props.className?.includes('image__cover')
        const loadingClass = (isProfileImage || isCoverImage) ? (isProfileImage ? ' image__profile--loading' : ' image__cover--loading') : ''
        return <div {...props} className={`${props.className}${loadingClass}`} style={{...props.style, display: isVisible ? 'block' : 'none'}}></div>
    }
    return <img {...props} style={{...props.style, display: isVisible ? 'block' : 'none'}} alt='' src={imageSrc} onError={() => setHasError(true)}></img>
}
export default Image
